import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router';
import { consumeAuthIntent, resolveReturnTo } from '@/features/auth/auth-intent';
import { resolveAccountEmail } from '@/features/auth/account-email';
import { requestWebEmailOtp, verifyWebEmailOtp } from '@/features/auth/auth.api';
import { formatOtpFlowError } from '@/features/auth/error-format';
import { buildClubHref, fetchPaywallOffer } from '@/features/catalog/catalog.api';
import { useAuthStore } from '@/features/auth/store';
import { writeStoredWebSession } from '@/features/auth/web-session-storage';
import { PageFrame } from '@/shared/components/ui/PageFrame';
import { MiniappSurface } from '@/shared/components/ui/MiniappSurface';
import { PrimaryButton } from '@/shared/components/ui/PrimaryButton';

const RESEND_COOLDOWN_SECONDS = 30;

function normalizeEmail(email: string) {
  return email.trim().toLowerCase();
}

function normalizeOtpCode(code: string) {
  return code.replace(/\D/g, '').slice(0, 6);
}

async function formatAuthError(error: unknown) {
  return formatOtpFlowError(error, {
    invalidEmail: 'Введите корректный email, например name@example.com.',
    invalidCode: 'Неверный код. Проверьте письмо и попробуйте ещё раз.',
    expiredCode: 'Код истёк. Запросите новый одноразовый код.',
    unavailable: 'Сервис входа временно недоступен. Попробуйте ещё раз.',
    generic: 'Не удалось завершить вход. Попробуйте ещё раз.',
  });
}

function extractPaywallReturnTarget(
  returnTo: string,
): { clubReference: string; source: 'catalog' | 'my-clubs' } | null {
  try {
    const url = new URL(returnTo, 'https://app.inhound.club');
    const match = /^\/paywall\/([^/]+)$/.exec(url.pathname);
    if (!match?.[1]) {
      return null;
    }

    return {
      clubReference: decodeURIComponent(match[1]),
      source: url.searchParams.get('source') === 'my-clubs' ? 'my-clubs' : 'catalog',
    };
  } catch {
    return null;
  }
}

async function resolvePostLoginDestination(returnTo: string): Promise<string> {
  const paywallTarget = extractPaywallReturnTarget(returnTo);
  if (!paywallTarget) {
    return returnTo;
  }

  try {
    const offer = await fetchPaywallOffer(paywallTarget.clubReference);
    if (offer.accessStatus === 'owned') {
      return buildClubHref(offer.club, paywallTarget.source);
    }
  } catch {
    // Fall back to the original return target when revalidation fails.
  }

  return returnTo;
}

export function AuthPage() {
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const runtime = useAuthStore((s) => s.runtime);
  const authUser = useAuthStore((s) => s.user);
  const setAuth = useAuthStore((s) => s.setAuth);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [step, setStep] = useState<'request' | 'verify'>('request');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [postAuthRedirect, setPostAuthRedirect] = useState<string | null>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const shouldFocusEmailRef = useRef(false);
  const activeOtpVerificationRef = useRef<string | null>(null);
  const autoSubmittedOtpRef = useRef<string | null>(null);

  const queryReturnTo = useMemo(() => resolveReturnTo(location.search), [location.search]);
  const paywallReturnTarget = useMemo(
    () => (queryReturnTo ? extractPaywallReturnTarget(queryReturnTo) : null),
    [queryReturnTo],
  );
  const allowTelegramEmailAuth = runtime === 'telegram' && Boolean(paywallReturnTarget);
  const accountEmail = useMemo(() => resolveAccountEmail(authUser), [authUser]);
  const requiresTelegramEmailLink = allowTelegramEmailAuth && !accountEmail;

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  useEffect(() => {
    if (step !== 'request' || !shouldFocusEmailRef.current) {
      return;
    }

    shouldFocusEmailRef.current = false;
    emailInputRef.current?.focus();
  }, [step]);

  const handleVerifyOtp = useCallback(
    async (codeOverride?: string) => {
      const normalizedCode = normalizeOtpCode(codeOverride ?? code);

      if (!sentTo || !normalizedCode) {
        setErrorMessage('Введите код из письма.');
        return;
      }

      if (activeOtpVerificationRef.current === normalizedCode) {
        return;
      }

      activeOtpVerificationRef.current = normalizedCode;
      setErrorMessage(null);
      setIsSubmitting(true);

      try {
        const result = await verifyWebEmailOtp(sentTo, normalizedCode);
        writeStoredWebSession({ accessToken: result.accessToken });
        setAuth(result.accessToken, result.user, runtime);
        const returnTo = queryReturnTo ?? consumeAuthIntent('/profile');
        const destination = await resolvePostLoginDestination(returnTo);
        setPostAuthRedirect(destination);
      } catch (error) {
        setErrorMessage(await formatAuthError(error));
      } finally {
        activeOtpVerificationRef.current = null;
        setIsSubmitting(false);
      }
    },
    [code, queryReturnTo, runtime, sentTo, setAuth],
  );

  useEffect(() => {
    if (step !== 'verify' || isSubmitting || !sentTo) {
      return;
    }

    const normalizedCode = normalizeOtpCode(code);
    if (normalizedCode.length !== 6 || autoSubmittedOtpRef.current === normalizedCode) {
      return;
    }

    autoSubmittedOtpRef.current = normalizedCode;
    void handleVerifyOtp(normalizedCode);
  }, [code, handleVerifyOtp, isSubmitting, sentTo, step]);

  if (runtime === 'telegram' && !allowTelegramEmailAuth) {
    return <Navigate to="/profile" replace />;
  }

  if (postAuthRedirect) {
    return <Navigate to={postAuthRedirect} replace />;
  }

  if (isAuthenticated && !isSubmitting && !requiresTelegramEmailLink) {
    return <Navigate to={queryReturnTo ?? consumeAuthIntent('/profile')} replace />;
  }

  async function handleRequestOtp() {
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) {
      setErrorMessage('Введите email, на который нужно отправить код.');
      return;
    }

    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      await requestWebEmailOtp(normalizedEmail);
      setSentTo(normalizedEmail);
      setStep('verify');
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setCode('');
      autoSubmittedOtpRef.current = null;
    } catch (error) {
      setErrorMessage(await formatAuthError(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleResendOtp() {
    if (!sentTo || cooldown > 0) return;

    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      await requestWebEmailOtp(sentTo);
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setCode('');
      autoSubmittedOtpRef.current = null;
    } catch (error) {
      setErrorMessage(await formatAuthError(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleUseDifferentEmail() {
    if (isSubmitting) return;

    setStep('request');
    setCode('');
    setErrorMessage(null);
    setCooldown(0);
    setSentTo('');
    activeOtpVerificationRef.current = null;
    autoSubmittedOtpRef.current = null;
    shouldFocusEmailRef.current = true;
  }

  return (
    <PageFrame container="readable-content" className="space-y-4">
      <div className="space-y-1">
        <h1 className="text-[28px] font-black uppercase leading-[0.9] tracking-[0.01em] text-[var(--foreground)] min-[430px]:text-[32px] min-[430px]:tracking-[0.02em]">
          ВОЙТИ / ЗАРЕГИСТРИРОВАТЬСЯ
        </h1>
        <p className="text-[14px] leading-6 text-[rgba(26,26,26,0.58)]">
          Войдите по email, чтобы открыть купленные клубы, продолжить чтение и завершить покупку в
          вашем аккаунте. Мы отправим одноразовый код на почту.
        </p>
      </div>

      <MiniappSurface className="space-y-4 px-5 py-6">
        <div>
          <label className="block space-y-2">
            <span className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[rgba(26,26,26,0.48)]">
              Email
            </span>
            <input
              ref={emailInputRef}
              type="email"
              inputMode="email"
              autoComplete="email"
              value={step === 'verify' ? sentTo : email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={step === 'verify' || isSubmitting}
              placeholder="you@example.com"
              className="h-12 w-full rounded-[18px] border border-[rgba(26,26,26,0.08)] bg-[rgba(255,255,255,0.82)] px-4 text-[14px] outline-none transition focus:border-[rgba(26,26,26,0.22)]"
            />
          </label>

          {step === 'verify' && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleUseDifferentEmail}
                disabled={isSubmitting}
                className="inline-flex min-h-11 items-center whitespace-nowrap rounded-lg px-1 text-right text-[13px] font-medium text-[rgba(26,26,26,0.68)] outline-none transition hover:text-[rgba(26,26,26,0.78)] focus-visible:[outline:2px_solid_rgba(107,78,230,0.65)] focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Указать другой email
              </button>
            </div>
          )}
        </div>

        {step === 'verify' && (
          <label className="block space-y-2">
            <span className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[rgba(26,26,26,0.48)]">
              Одноразовый код
            </span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(event) => {
                const normalizedCode = normalizeOtpCode(event.target.value);
                setCode(normalizedCode);
                if (normalizedCode.length < 6) {
                  autoSubmittedOtpRef.current = null;
                }
              }}
              disabled={isSubmitting}
              placeholder="******"
              className="h-12 w-full rounded-[18px] border border-[rgba(26,26,26,0.08)] bg-[rgba(255,255,255,0.82)] px-4 text-[14px] outline-none transition focus:border-[rgba(26,26,26,0.22)]"
            />
          </label>
        )}

        {errorMessage && (
          <p className="rounded-[16px] bg-[rgba(232,93,42,0.1)] px-4 py-3 text-[13px] leading-5 text-[rgba(123,49,21,0.92)]">
            {errorMessage}
          </p>
        )}

        <div className="space-y-2">
          <PrimaryButton
            onClick={() => void (step === 'request' ? handleRequestOtp() : handleVerifyOtp())}
            className="w-full"
            disabled={isSubmitting}
          >
            {step === 'request' ? 'Получить код' : 'Подтвердить вход'}
          </PrimaryButton>

          {step === 'verify' && (
            <PrimaryButton
              tone="ghost"
              onClick={() => void handleResendOtp()}
              className="w-full"
              disabled={isSubmitting || cooldown > 0}
            >
              {cooldown > 0
                ? `Отправить код повторно через ${cooldown}с`
                : 'Отправить код повторно'}
            </PrimaryButton>
          )}
        </div>
      </MiniappSurface>
    </PageFrame>
  );
}
